import React, { useEffect, useState } from 'react';
import { t } from 'ttag';
import classNames from 'classnames';
import { BrowserRouter } from 'react-router-dom';

import { getClientLocale, I18NProvider } from '@utils/i18n';
import Title from '@ui/Title';
import { useTabletOrWider } from '@utils/mediaQuery';
import { Button } from '@ui/Button';
import { enumerate } from '@utils/common';
import ProductCharacteristicsFilter from '@containers/ProductCharacteristicsFilter';
import { FeedBrandFilterNew } from '@components/FeedBrandFilterNew';
import { FeedTagFilter } from '@components/FeedTagFilter';
import FeedSizeFilter from '@components/FeedSizeFilter';
import apolloWrapper from '@hoc/apolloWrapper';
import CloseIcon from '@svg/close.svg';

import { HiddenFilterBlock } from '../../../../utils';
import PriceFilter from '../PriceFilter';
import ColorFilter from '../ColorFilter';
import ConditionFilter from '../ConditionFilter';
import CityFilter from '../CityFilter';

import { EventStream, EventStreamEvents } from './types';

import s from './Aside.sss';

interface BrandOption {
  id: number,
  name: string,
  isUkrainian?: boolean,
}

type FilterStateType = {
  brands: number[];
  cities: number[];
  price_from: number | null;
  price_to: number | null;
  sizes: number[];
  prices: number[];
  conditions: number[];
  characteristics: number[];
  colors: number[];
  ukrainian: null | boolean;
  free_shipping: boolean | null;
  is_on_sale: boolean | null;
};

interface Size {
  id: number,
  title: string,
  title_short: string,
}

type AsideProps = {
  sizeFilters: Size[];
  eventStream: EventStream;
  conditionFilters: number[];
  brandFilters: {
    topBrands: BrandOption[],
    brands: BrandOption[],
    popularBrands: BrandOption[],
  };
  cityFilters: number[];
  data: {
    config: {
      userIsAuthenticated: boolean;
    };
    FilterState: FilterStateType;
    ClearableFilters: string[];
    userCity: {
      label: string,
      value: number,
    } | null;
    brandSearchText: string,
    isCityModalOpen: boolean;
    brandSuggestionsIsLoading: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    brandSuggestions: any;
    selectedBrands: Map<number, BrandOption>;
    catalogSlug?: string;
    mostFrequentCatalogSlug?: string;
    characteristics?: number[];
  };
  priceFilters: number[];
  colorFilters: number[];
};

const AsideComponent: React.FC<AsideProps> = apolloWrapper(({
  eventStream,
  sizeFilters,
  priceFilters,
  conditionFilters,
  colorFilters,
  cityFilters,
  brandFilters,
  data,
  data: {
    FilterState: state,
    ClearableFilters: clearableFilters,
    userCity,
    mostFrequentCatalogSlug,
  },
}) => {
  const { location } = window;
  const isTabletOrWider = useTabletOrWider();
  const [total, setTotal] = useState<string>('');
  const setTotalFromNode = () => {
    const totalNode = document.querySelector('#product-count');
    if (totalNode) {
      const productCount = totalNode.getAttribute('data-count');
      if (productCount) {
        setTotal(productCount);
      }
    }
  };

  const handleBrandInputChange = (name: string) => {
    eventStream.next({
      action: EventStreamEvents.brandInputChange,
      eventStream,
      config: data.config,
      value: name,
    });
  };

  const handleBrandSelect = (brand: { id: number, name: string }) => {
    eventStream.next({
      action: EventStreamEvents.brandChanged,
      brand,
    });
  };

  const handleBinaryFilterChange = (value: string) => {
    let action;
    let newValue;
    let referrer;

    if (value === 'freeShipping') {
      action = EventStreamEvents.setFreeShipping;
      newValue = state.free_shipping ? null : true;
    } else if (value === 'isOnSale') {
      action = EventStreamEvents.setIsOnSale;
      newValue = state.is_on_sale ? null : true;
    } else if (value === 'ukrainian') {
      action = EventStreamEvents.setMadeInUkraineFilter;
      newValue = state.ukrainian ? null : true;
      referrer = 'filter_tag';
    }

    if (action) {
      eventStream.next({
        action,
        value: newValue,
        referrer,
      });
    }
  };

  const handleSizeChanged = (id: number) => {
    eventStream.next({
      action: EventStreamEvents.setSizeFilter,
      id,
    });
  };

  useEffect(
    () => {
      const count = document.querySelector('.js-pjax-mount');
      setTotalFromNode();
      const observer = new MutationObserver(setTotalFromNode);
      const config = {
        childList: true,
      };
      if (count) {
        observer.observe(count, config);
      }
      return () => observer.disconnect();
    },
    [],
  );

  const getSizeOptions = () => {
    // ref - shafa/apps/graph/utils/common.py
    // # split size title in format:
    // # '36 / 8 / S' -> '36' and '8/S'
    // # '26 / 27' -> '26/27'
    // # 'One Size' -> 'One Size'

    return sizeFilters.map((size) => {
      const sizesNames = size.title.replaceAll(' / ', '/').split('/');
      const isComplexSize = (size.title.match(new RegExp('\/', 'g')) || []).length > 1;
      const primarySizeName = isComplexSize ? sizesNames[0] : sizesNames.join('/');
      const secondarySizeName = isComplexSize ? sizesNames.slice(1).join('/') : null;

      return {
        id: size.id,
        primarySizeName,
        secondarySizeName,
      };
    });
  };

  return (
    (
      <div className={s.root}>
        <div className="b-filter__header-content">
          <button
            type="button"
            className={s.close}
            onClick={HiddenFilterBlock}
          >
            <CloseIcon
              className={s.closeIcon}
              width='16'
              height='16'
            />
          </button>
          <Title type="h2" bold={false}>{t`Фільтри`}</Title>
        </div>
        {!isTabletOrWider && location.search && (
        <div className={classNames(s.clearAllBlock, 'b-filter__section')}>
          <div className={classNames(s.clearAll, 'js-catalog-filter-remove-all')}>{t`Скинути фільтри`}</div>
        </div>
        )}
        <section className={s.section}>
          <FeedTagFilter
            value={{
              freeShipping: !!state.free_shipping,
              isOnSale: !!state.is_on_sale,
              ukrainian: !!state.ukrainian,
            }}
            onClick={handleBinaryFilterChange}
          />
        </section>
        {sizeFilters && clearableFilters.includes('sizes') && (
          <FeedSizeFilter
            options={getSizeOptions()}
            actives={state.sizes}
            select={handleSizeChanged}
          />
        )}
        {clearableFilters.includes('conditions') && (
        <ConditionFilter
          eventStream={eventStream}
          checked={state.conditions}
          filters={conditionFilters}
        />
        )}
        {clearableFilters.includes('prices') && (
        <PriceFilter
          eventStream={eventStream}
          filters={priceFilters}
          checked={state.prices}
          from={state.price_from}
          to={state.price_to}
        />
        )}
        {clearableFilters.includes('colors') && (
        <ColorFilter
          eventStream={eventStream}
          checked={state.colors}
          filters={colorFilters}
        />
        )}
        {clearableFilters.includes('brands') && (
        <FeedBrandFilterNew
          className="b-filter__section"
          value={Array.from(data.selectedBrands, (entry) => entry[1])}
          inputValue={data.brandSearchText}
          onInputChange={handleBrandInputChange}
          onChange={handleBrandSelect}
          defaultOptions={{
            topBrands: brandFilters?.topBrands || [],
            brands: brandFilters?.brands || [],
          }}
          suggests={data.brandSuggestions || []}
        />
        )}
        {data.config.userIsAuthenticated && clearableFilters.includes('cities') && (
        <CityFilter
          eventStream={eventStream}
          userCity={userCity}
          cities={cityFilters}
          checked={userCity != null && state.cities.includes(userCity.value)}
          isCityModalOpen={data.isCityModalOpen}
          config={data.config}
        />
        )}

        {!!mostFrequentCatalogSlug && (
        <ProductCharacteristicsFilter
          onCharacteristicChoiceSelect={
            (choiceId: number) => {
              eventStream.next({
                action: EventStreamEvents.characteristicChanged,
                choiceId,
              });
            }
          }
          value={state.characteristics || []}
          catalogSlug={mostFrequentCatalogSlug}
        />
        )}
        {!isTabletOrWider && (
        <div className={s.mobileButtons}>
          <Button
            className={s.buttonSeeFilters}
            theme="primary"
            onClick={HiddenFilterBlock}
          >
            {t`Показати`}
            {' '}
            {total}
            {' '}
            {enumerate(Number(total), [t`річ`, t`речі`, t`речей`])}
          </Button>
        </div>
        )}
      </div>
    )
  );
});

export const Aside: React.FC<AsideProps> = (props) => {
  const { lang, routerBasename } = getClientLocale();
  return (
    <BrowserRouter basename={routerBasename}>
      <I18NProvider lang={lang}>
        {
          () => (
            <AsideComponent {...props} />
          )
        }
      </I18NProvider>
    </BrowserRouter>
  );
};
